import * as XLSX from 'xlsx';
/* ★ §E1 — the ONE IS-category normalizer, imported, never copied (§6). It is the same
 * module AdminPage.js:26 imports, and it is a leaf (no imports of its own), so there is
 * no cycle. The parser must never reach into pages/ — this is the shared util both
 * sides already agree on. */
import { matchISCategory, IS_CATEGORY_LABELS } from './isCategoryMatch';
import { normalizeHeader, aliasSet, monthNameToNum, parseHeaderMonth } from './templateHeaders';
import { specColumnAliases } from './templateSpecs';

const r2 = n => Math.round(n * 100) / 100;

/* MONTH_MAP + monthNameToNum moved to templateHeaders.js (W-062 pass 2; identical text) -- one copy for every parser. */

// ── Helpers ──────────────────────────────────────────────────

function isYearLike(val) {
    const n = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
    return !isNaN(n) && n >= 1990 && n <= 2099;
}

function isMonthName(val) {
    return !!monthNameToNum(val);
}

function isTotalRow(label) {
    if (!label) return false;
    const l = String(label).toLowerCase().trim();
    return /\btotal\b/.test(l) || /\bsubtotal\b/.test(l) || /\bgrand total\b/.test(l);
}

function isFormulaCell(cell) {
    if (!cell) return false;
    if (cell.f) return true;
    if (typeof cell.v === 'string' && cell.v.trim().startsWith('=')) return true;
    return false;
}

function cellValue(cell) {
    if (!cell) return null;
    if (cell.t === 'n') return cell.v;
    if (cell.t === 's') return cell.v;
    if (cell.t === 'b') return cell.v;
    if (cell.t === 'd') return cell.v;
    return cell.v;
}

function cellNumeric(cell) {
    if (!cell) return 0;
    const v = cell.v;
    if (typeof v === 'number') return r2(v);
    if (typeof v === 'string') {
        const cleaned = v.replace(/[$,()]/g, '').trim();
        if (cleaned === '' || cleaned === '-') return 0;
        // Handle parenthetical negatives: (123) → -123
        const isNeg = v.includes('(') && v.includes(')');
        const n = parseFloat(cleaned);
        if (isNaN(n)) return 0;
        return r2(isNeg ? -Math.abs(n) : n);
    }
    return 0;
}

// ── Date header detection ────────────────────────────────────

function detectDateHeaders(sheet) {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const maxRow = Math.min(range.e.r, 20); // scan first 20 rows
    const maxCol = range.e.c;

    let yearRow = -1;
    let monthRow = -1;
    let dateColumns = {}; // colIdx → YYYY-MM

    // Strategy 1: find two-row header (year row + month row)
    for (let r = 0; r <= maxRow; r++) {
        let yearCount = 0;
        let monthCount = 0;
        for (let c = 0; c <= maxCol; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];
            const val = cellValue(cell);
            if (val == null) continue;
            if (isYearLike(val)) yearCount++;
            if (isMonthName(val)) monthCount++;
        }
        if (yearCount >= 2 && yearRow === -1) yearRow = r;
        if (monthCount >= 3 && monthRow === -1) monthRow = r;
    }

    if (monthRow >= 0) {
        // Two-row: year row above month row, or same row
        const effYearRow = yearRow >= 0 && yearRow <= monthRow ? yearRow : monthRow;

        // Build year spans: track which year applies to each column
        let currentYear = null;
        const yearByCol = {};
        for (let c = 0; c <= maxCol; c++) {
            const addr = XLSX.utils.encode_cell({ r: effYearRow, c });
            const cell = sheet[addr];
            const val = cellValue(cell);
            if (val != null && isYearLike(val)) {
                currentYear = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
            }
            if (currentYear) yearByCol[c] = currentYear;
        }

        // Handle merged cells for year row
        if (sheet['!merges']) {
            for (const merge of sheet['!merges']) {
                if (merge.s.r <= effYearRow && merge.e.r >= effYearRow) {
                    const addr = XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c });
                    const cell = sheet[addr];
                    const val = cellValue(cell);
                    if (val != null && isYearLike(val)) {
                        const yr = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
                        for (let c = merge.s.c; c <= merge.e.c; c++) {
                            yearByCol[c] = yr;
                        }
                    }
                }
            }
        }

        // Map month columns
        for (let c = 0; c <= maxCol; c++) {
            const addr = XLSX.utils.encode_cell({ r: monthRow, c });
            const cell = sheet[addr];
            const val = cellValue(cell);
            const mo = monthNameToNum(val);
            if (mo && yearByCol[c]) {
                dateColumns[c] = `${yearByCol[c]}-${mo}`;
            }
        }

        if (Object.keys(dateColumns).length >= 3) {
            return { dateColumns, dataStartRow: monthRow + 1 };
        }
    }

    // Strategy 2: single-row with "Jan 2021" or "January 2021" style
    for (let r = 0; r <= maxRow; r++) {
        const cols = {};
        for (let c = 0; c <= maxCol; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];
            const val = cellValue(cell);
            if (val == null) continue;
            const s = String(val).trim();
            // Match "Jan 2021", "January 2021", "Jan-21", "Jan-2021"
            const m = s.match(/^([A-Za-z]+)[\s\-./]+(\d{2,4})$/);
            if (m) {
                const mo = monthNameToNum(m[1]);
                let yr = parseInt(m[2], 10);
                if (yr < 100) yr += 2000;
                if (mo && yr >= 1990 && yr <= 2099) {
                    cols[c] = `${yr}-${mo}`;
                }
            }
            // Match Excel date serial numbers
            if (cell && cell.t === 'd') {
                const d = new Date(val);
                const yr = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, '0');
                if (yr >= 1990 && yr <= 2099) {
                    cols[c] = `${yr}-${mo}`;
                }
            }
        }
        if (Object.keys(cols).length >= 3) {
            return { dateColumns: cols, dataStartRow: r + 1 };
        }
    }

    return { dateColumns: {}, dataStartRow: 0 };
}

// ── Canonical header-named format (AE Model template) ────────
// row0: is_category | display_group | gl_line_name | 2021-01 | 2021-02 | … (ISO YYYY-MM columns).
// Detected by an explicit header row (is_category + gl_line_name) plus >=1 MONTH header (>=3 until 2026-11-02); read
// by column NAME, not position — the header-name analog of parsePrepaidSheet (Session 4c). Tolerates
// blanks: a blank display_group → null (never ""); a blank value cell → 0.
/* THE HEADER ALIASES THIS PARSER READS -- the spec's own arrays (templateSpecs.js), by reference; the spec gate
 * asserts it. Matched through the ONE header rule (templateHeaders.normalizeHeader), which replaced this file's
 * own rule (spaces -> "_" only, so "IS-Category" used to miss). W-062 pass 2. */
export const FINANCIAL_HEADER_COLUMNS = {
    'is.is_category': specColumnAliases('is', 'is_category'),
    'is.display_group': specColumnAliases('is', 'display_group'),
    'is.gl_line_name': specColumnAliases('is', 'gl_line_name'),
    'bs.is_category': specColumnAliases('bs', 'is_category'),
    'bs.display_group': specColumnAliases('bs', 'display_group'),
    'bs.gl_line_name': specColumnAliases('bs', 'gl_line_name'),
    // located so it is not mistaken for anything else, then ignored (a vendor column on an IS/BS sheet)
    'vendor.vendor_name': specColumnAliases('vendor', 'vendor_name'),
};
const FH = Object.fromEntries(Object.entries(FINANCIAL_HEADER_COLUMNS).map(([k, a]) => [k, aliasSet(a)]));
// `tpl` is 'is' or 'bs' -- which template's aliases to read (today the two carry the same lists).
function detectCanonicalHeader(sheet, tpl) {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const maxRow = Math.min(range.e.r, 20);
    const maxCol = range.e.c;
    for (let r = 0; r <= maxRow; r++) {
        const cols = { is_category: null, display_group: null, gl_line_name: null, vendor_name: null };
        const dateColumns = {};
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            const val = cellValue(cell);
            if (val == null) continue;
            const h = normalizeHeader(val);
            if (cols.is_category == null && FH[tpl + '.is_category'].has(h)) cols.is_category = c;
            else if (cols.display_group == null && FH[tpl + '.display_group'].has(h)) cols.display_group = c;
            else if (cols.gl_line_name == null && FH[tpl + '.gl_line_name'].has(h)) cols.gl_line_name = c;
            else if (cols.vendor_name == null && FH['vendor.vendor_name'].has(h)) cols.vendor_name = c;
            else {
                /* A MONTH HEADER, read by the ONE month reader. This used to accept only TEXT "YYYY-MM", so a sheet whose
                 * month headers were real Excel dates, "2023-01-01" or "Jan-23" failed this detector and imported as
                 * ZERO lines (or fell to the legacy parser and was misread). */
                const ym = parseHeaderMonth(val, cell);
                if (ym) dateColumns[c] = ym;
            }
        }
        /* 2026-11-02 (the one-month start): ONE readable month header is enough here. The two template headers already
         * identify the sheet, and the upload check (specCheck B10) now asks the stronger question -- do the month columns
         * cover the company's own months? -- so a brand-new company's one-month file is read instead of refused. This
         * read ">= 3" (the legacy month-name readers above keep their own ">= 3": they have no header to go on). */
        if (cols.is_category != null && cols.gl_line_name != null && Object.keys(dateColumns).length >= 1) {
            return { cols, dateColumns, headerRow: r };
        }
    }
    return null;
}
/* THE SHEET AS THE CHECK READS IT (W-068 pass 3): every cell's raw value, indexed by the ABSOLUTE 0-based row and
 * column, so row i is spreadsheet row i + 1 -- the same numbering this parser's own flags use. utils/specCheck.js
 * reads only this grid plus the header->column map below; it never re-detects a header. */
function sheetGrid(sheet) {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const rows = [];
    for (let r = 0; r <= range.e.r; r++) {
        const row = [];
        for (let c = 0; c <= range.e.c; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            row.push(cell ? cell.v : null);
        }
        rows.push(row);
    }
    return rows;
}
/* A sheet that carries the template's is_category or line-name HEADER but failed canonical detection (the other one
 * missing, or no readable month header -- fewer than 3 until 2026-11-02) is a BROKEN template, not a legacy export: the legacy reader below
 * would misread it (or read nothing). Found here, with the same header rule and month reader, so the check can name what
 * is missing. Returns null for a true legacy sheet (neither header in the first 21 rows). */
function detectBrokenTemplate(sheet, tpl) {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const maxRow = Math.min(range.e.r, 20);
    for (let r = 0; r <= maxRow; r++) {
        const cols = { is_category: null, display_group: null, gl_line_name: null };
        const monthCols = {};
        for (let c = 0; c <= range.e.c; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            const val = cellValue(cell);
            if (val == null) continue;
            const h = normalizeHeader(val);
            if (cols.is_category == null && FH[tpl + '.is_category'].has(h)) cols.is_category = c;
            else if (cols.display_group == null && FH[tpl + '.display_group'].has(h)) cols.display_group = c;
            else if (cols.gl_line_name == null && FH[tpl + '.gl_line_name'].has(h)) cols.gl_line_name = c;
            else { const ym = parseHeaderMonth(val, cell); if (ym) monthCols[c] = ym; }
        }
        // is_category OR the line-name header marks the template: a raw month-name export carries neither.
        if (cols.is_category != null || cols.gl_line_name != null) return { template: tpl, layout: 'broken', headerRow: r, cols, monthCols, rows: sheetGrid(sheet) };
    }
    return null;
}
function canonCellText(sheet, r, c) {
    if (c == null) return null;
    const v = cellValue(sheet[XLSX.utils.encode_cell({ r, c })]);
    if (v == null) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
}
function parseCanonicalSheet(sheet, canon, statementType) {
    const { cols, dateColumns } = canon;
    const dateCols = Object.keys(dateColumns).map(Number);
    const months = [...new Set(Object.values(dateColumns))].sort();
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const flags = [];
    const lines = [];

    /* ★★ §E1 — A DELETED OPTIONAL COLUMN IS INDISTINGUISHABLE FROM AN EMPTY ONE, AND THIS
     * IS THE ONLY SCOPE THAT STILL KNOWS THE DIFFERENCE.
     *
     * `detectCanonicalHeader` seeds every entry of `cols` with null and assigns an index
     * only when it actually sees the header text, so RIGHT HERE:
     *     cols.display_group === null    -> the column is ABSENT from the workbook
     *     cols.display_group === <Number> -> the column is PRESENT (cells may still be blank)
     * One function later that distinction is GONE: canonCellText returns null for
     * column-absent, cell-empty and cell-whitespace alike, and this function returns only
     * { months, lines, flags } — `cols` is discarded at the parse boundary. ⛔ SO NO
     * DOWNSTREAM CHECK CAN EVER RECOVER IT. If it is not asked here it cannot be asked.
     *
     * WHY IT MATTERS (measured 2026-09-08): on a workbook with no display_group column a
     * multi-group section orphans its vendor lines — cid=3's shape renders SIX leaves
     * outside the Sales+Marketing sub-total, at the top of S&M — and ★ NO NUMBER MOVES,
     * because the orphan fold still totals them correctly. A golden ties cleanly over a
     * visibly wrong statement (§E0), and the only existing signals are a console.warn and
     * a diagnostics gate, neither of which an operator ever sees.
     *
     * ⛔ INFORMATIONAL, NEVER BLOCKING. A tenant with no sub-groups is LEGITIMATE input —
     * cid=3's own workbook carries no such column and is valid — so refusing would fire on
     * correct data (§12.5 shape 10, the miscalibration this project keeps fixing). Step 2b
     * routes on `flags.length > 0` and its "These look correct, continue →" button IS the
     * acknowledgement; "Re-upload files" is the other way out. Modelled on the
     * `unknown_category` flag below — the register's other §E1 entry, which likewise names
     * an OPERATOR MISTAKE rather than a parser observation and states both ways out.
     *
     * ⛔ ONCE PER SHEET, NEVER PER ROW. Pushed before the row loop deliberately: a per-row
     * notice would produce one identical sentence per GL line, which is exactly the wall of
     * text the MSG_NO_CATEGORY collapse in AdminPage's Step-6 review exists to undo.
     * ⓘ It can only fire on a CANONICAL sheet — a legacy month-name workbook never reaches
     *   this function, and has no such column as a concept. */
    if (cols.display_group == null) {
        flags.push({
            type: 'no_display_group_column', severity: 'info',
            row: canon.headerRow + 1,
            label: 'display_group',
        });
    }

    for (let r = canon.headerRow + 1; r <= range.e.r; r++) {
        const glName = canonCellText(sheet, r, cols.gl_line_name);
        /* A row with no gl_line_name is not read. If it carries data, the template check names it (B2) and blocks the
         * upload -- it used to vanish here without a word. */
        if (!glName) continue;
        /* W-068 F1 (Brian 09-29): a row that LOOKS LIKE A TOTAL is KEPT, no longer dropped here. The template check
         * flags it, and Step 3 pre-sets it to Skip marked "suggested -- looks like a total row -- confirm or change", so
         * a real line such as "Total Rewards Platform" can be kept by the operator instead of disappearing. (This read
         * `if (isTotalRow(glName)) { flags.push({ type: 'subtotal', ... }); continue; }` -- the row was dropped with an
         * info notice.) The LEGACY reader below still drops total rows: a raw export's totals are totals. */
        const category = canonCellText(sheet, r, cols.is_category);
        /* ★★ §E1 — NAME A CATEGORY WE CANNOT RECOGNISE, AT THE DOOR.
         *
         * Nothing else on the import path constrains this column: the cell is written
         * VERBATIM (canonCellText only trims), the payload and the server pass it
         * through, and until now no flag mentioned it. A mis-typed section ("Markting",
         * "Widgets Division") therefore travelled silently to Step 3, where it surfaced
         * only as a BLANK category and a generic "not mapped" — the operator could see
         * THAT something was wrong but not WHAT, and by then re-uploading the workbook
         * costs more than hand-mapping every row.
         *
         * ★ THIS IS A WARN, NOT A BLOCK, AND THAT IS DELIBERATE. Step 3 ALREADY refuses
         * to continue on an unrecognised category (it resolves to a blank is_category and
         * the unmapped-IS blocker fires), so a second gate here would be duplicate
         * machinery for an invariant that is already enforced. What was missing was the
         * NAME on the cause, early enough to fix the file instead of the mapping.
         *
         * ★ LENIENT BY RULE: flag only what `matchISCategory` cannot resolve AT ALL. A
         * LONG-FORM label ("Sales and Marketing") resolves to S&M and must NOT flag —
         * the onExpenseSection repair deliberately accepts exactly those, and flagging
         * them here would have the wizard call one import valid at read time and invalid
         * at upload time. "Revenue" resolves to Skip, which is a valid answer (it is
         * wired at Step 7), so it must not flag either. A BLANK cell is not an error
         * here — it is the D-2 "guess from the line name" path, and Step 3 owns it. */
        /* ★★ §E1 — THIS CHECK IS INCOME-STATEMENT-ONLY, AND THE GATE IS THE WHOLE FIX.
         *
         * `matchISCategory` resolves INCOME-STATEMENT sections. Run over a BALANCE SHEET it
         * flagged 18 of 40 lines (45%) of a CORRECT template — every Equity, Fixed Asset and
         * Intangible Asset row — while "Current Assets" and "Current Liabilities" stayed
         * silent only by MIS-resolving to "G&A". A check that fires on correct data is the
         * defect §12.5 names, not a safety net.
         *
         * ⛔ NO BS-SIDE CHECK REPLACES IT, AND THAT IS DELIBERATE RATHER THAN DEFERRED.
         * The operator names every category explicitly at Step 3, so an upload-time
         * vocabulary check is redundant to the operator's own choice (§E1), and the parser
         * stores the RAW string either way — nothing downstream consumes the match.
         *
         * ★ FAIL-OPEN ON PURPOSE (`!== 'BS'`, not `=== 'IS'`): the measured defect is
         * precisely and only the BS sheet, so only the BS sheet is excluded. Were
         * `statementType` ever absent, this check would still RUN — losing it silently is
         * the worse failure (§12.5 shape 2: a check that cannot fire reads as green). Both
         * production callers pass an explicit literal today. */
        if (category && statementType !== 'BS') {
            const resolved = matchISCategory(category);
            if (!IS_CATEGORY_LABELS.includes(resolved) && resolved !== 'Skip') {
                flags.push({ type: 'unknown_category', severity: 'warning', row: r + 1, label: glName, value: category });
            }
        }
        const displayGroup = canonCellText(sheet, r, cols.display_group); // blank → null
        const amounts = {};
        let allZero = true;
        for (const c of dateCols) {
            const amt = cellNumeric(sheet[XLSX.utils.encode_cell({ r, c })]); // blank cell → 0
            amounts[dateColumns[c]] = amt;
            if (amt !== 0) allZero = false;
        }
        if (allZero) flags.push({ type: 'zero_row', severity: 'info', label: glName });
        lines.push({ gl_name: glName, gl_category: category, display_group: displayGroup, amounts });
    }
    const specScan = { template: statementType === 'BS' ? 'bs' : 'is', layout: 'template', headerRow: canon.headerRow,
        cols: { is_category: cols.is_category, display_group: cols.display_group, gl_line_name: cols.gl_line_name },
        monthCols: dateColumns, rows: sheetGrid(sheet) };
    return { months, lines, flags, specScan };
}

// ── Sheet parsing ────────────────────────────────────────────

function parseSheet(sheet, statementType) {
    // Canonical header-named format first; fall back to the legacy month-name strategies.
    const canon = detectCanonicalHeader(sheet, statementType === 'BS' ? 'bs' : 'is');
    if (canon) return parseCanonicalSheet(sheet, canon, statementType);
    // W-068: a sheet carrying the template's header that could not be read as the template -- recorded for the check
    // (it blocks), while the legacy reader below still runs exactly as before.
    const broken = detectBrokenTemplate(sheet, statementType === 'BS' ? 'bs' : 'is');
    const withScan = (res) => (broken ? { ...res, specScan: broken } : res);
    const { dateColumns, dataStartRow } = detectDateHeaders(sheet);
    const months = [...new Set(Object.values(dateColumns))].sort();
    const flags = [];
    const lines = [];

    if (months.length === 0) {
        flags.push({ type: 'no_dates', severity: 'warning', row: 0, label: 'Could not detect date columns in sheet' });
        return withScan({ months, lines, flags });
    }

    // Detect merged cells
    if (sheet['!merges']) {
        for (const merge of sheet['!merges']) {
            if (merge.s.r >= dataStartRow) {
                flags.push({ type: 'merged', severity: 'warning', row: merge.s.r + 1, col: XLSX.utils.encode_col(merge.s.c) });
            }
        }
    }

    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const dateCols = Object.keys(dateColumns).map(Number);
    const minDataCol = Math.min(...dateCols);

    let currentCategory = null;
    // Data rows emitted under currentCategory — drives the suspicious_header guard below.
    let rowsInCurrentCategory = 0;

    for (let r = dataStartRow; r <= range.e.r; r++) {
        // Find label in first few columns (before date columns)
        let label = null;
        let labelCol = -1;
        for (let c = 0; c < Math.min(minDataCol, 4); c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];
            const v = cellValue(cell);
            if (v != null && String(v).trim() !== '') {
                label = String(v).trim();
                labelCol = c;
                break;
            }
        }

        if (!label) {
            if (labelCol === -1) {
                // Check if any data cells have values (continuation row)
                let hasData = false;
                for (const c of dateCols) {
                    const addr = XLSX.utils.encode_cell({ r, c });
                    const cell = sheet[addr];
                    if (cell && cellNumeric(cell) !== 0) { hasData = true; break; }
                }
                if (!hasData) continue; // truly blank row
                flags.push({ type: 'blank_row', severity: 'warning', row: r + 1 });
                continue;
            }
            continue;
        }

        // Skip total/subtotal rows
        if (isTotalRow(label)) {
            flags.push({ type: 'subtotal', severity: 'info', row: r + 1, label });
            continue;
        }

        /* ★★ IS THIS A SECTION HEADER? (label only, NO CELLS)
         *
         * THIS PREDICATE USED TO READ:
         *     if (cell && cellNumeric(cell) !== 0) { allBlank = false; break; }
         * — i.e. a cell CONTAINING ZERO counted as blank. **A ZERO IS DATA.** A line that
         * legitimately booked $0.00 every month is not a section header.
         *
         * WHAT THAT COST (AE Model, verified): Data Science booked zero bonuses for all 27
         * months, so its "Bonuses" row was mistaken for a header. Two things then happened,
         * and the second is the dangerous one:
         *   1. the row was DISCARDED (`continue`) — the line vanished from the import; and
         *   2. currentCategory became 'Bonuses', so EVERY ROW BENEATH IT inherited a bogus
         *      gl_category until the next real header. Data Science's Payroll Taxes and
         *      Benefits were filed under a category called "Bonuses".
         * ONE all-zero row silently deleted itself AND corrupted its neighbours. The
         * corruption CASCADES; it does not stop at the offending row.
         *
         * AE Model escaped harm only because its is_actuals came from the Session-4b PYTHON
         * parser (which reads the template's explicit display_group column). For a NEW
         * tenant THIS parser IS the pipeline, and the corrupted rows would get no
         * auto-suggestion, be left unmapped, and never appear on the income statement.
         *
         * THE CORRECT TEST — presence, not value:
         *   SheetJS omits an empty cell from the sheet object ENTIRELY (sheet[addr] is
         *   undefined). A zero cell is PRESENT: { t:'n', v:0 }. So a header row has NO cells
         *   in the date columns; a zero row has cells whose value is 0.
         *   We therefore ask "does ANY date column have a cell with a real value?" and treat
         *   only '' / null / undefined as no-value. `cellNumeric` is deliberately NOT used
         *   here — it maps a missing cell AND a zero cell to the same 0, which is precisely
         *   the conflation that caused the bug. Re-introducing it in any form reproduces it
         *   in a new costume. */
        let hasAnyCell = false;
        for (const c of dateCols) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            if (!cell) continue;                                  // truly absent → no data
            const v = cell.v;
            if (v === undefined || v === null) continue;          // present but empty
            if (typeof v === 'string' && v.trim() === '') continue;
            hasAnyCell = true;                                    // a 0 lands HERE — it is data
            break;
        }
        if (!hasAnyCell) {
            /* ★ FAIL-LOUD GUARD: a header appearing INSIDE a section that already has data
             * rows is suspicious — a real workbook does not nest sections that way, and it is
             * the exact shape the old bug produced. Flag it for the Step-2 quality review
             * rather than silently re-pointing currentCategory. We still honour it (a genuine
             * mid-sheet section IS legal), but the operator gets told. */
            if (currentCategory && rowsInCurrentCategory > 0) {
                flags.push({
                    type: 'suspicious_header', severity: 'warning', row: r + 1, label,
                    message: `"${label}" has no values and was read as a new section heading, but ` +
                        `"${currentCategory}" already had ${rowsInCurrentCategory} line(s). If "${label}" is ` +
                        `actually a line that happens to be zero every month, it will be dropped and the ` +
                        `lines under it mis-filed. Check the sheet.`,
                });
            }
            currentCategory = label;
            rowsInCurrentCategory = 0;
            continue;
        }

        // Extract amounts
        const amounts = {};
        let allZero = true;
        let hasFormula = false;
        for (const c of dateCols) {
            const month = dateColumns[c];
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];

            if (isFormulaCell(cell)) {
                hasFormula = true;
                flags.push({ type: 'formula', severity: 'warning', row: r + 1, cell: addr, value: cell.f || String(cell.v || '') });
            }

            const amt = cellNumeric(cell);
            amounts[month] = amt;
            if (amt !== 0) allZero = false;
        }

        /* ★ AN ALL-ZERO ROW IS RETAINED, NOT DROPPED.
         *
         * This used to `continue` — so a line that booked $0.00 every month was discarded
         * outright. That is the SAME conflation the header predicate above made (zero ==
         * nothing), just one stage later, and it has the same consequence: the line does not
         * exist, so it cannot be mapped, so it can never appear on the statement — not even
         * with a forecast, which is exactly what a zero-history line usually needs.
         *
         * A row that is zero across all of history is still a real chart-of-accounts line.
         * Keep it, keep the flag (the Step-2 quality review still surfaces it), and let the
         * operator decide: give it a category, or mark it Skip — a decision that now
         * PERSISTS (see AdminPage's save filter) and is enforced by the unmapped-IS blocker.
         * "Skip is a valid decision; silence is not." */
        if (allZero) {
            flags.push({ type: 'zero_row', severity: 'info', label });
        }

        lines.push({
            gl_name: label,
            gl_category: currentCategory,
            amounts,
        });
        rowsInCurrentCategory += 1;
    }

    return withScan({ months, lines, flags });
}

// ── Main export ──────────────────────────────────────────────

export default function parseFinancialExcel(buffer) {
    const workbook = XLSX.read(buffer, { type: 'array', cellFormula: true, cellStyles: false });
    const sheetNames = workbook.SheetNames;

    // Detect IS and BS sheets
    let isSheet = null;
    let bsSheet = null;

    for (const name of sheetNames) {
        const lower = name.toLowerCase().trim();
        if (!isSheet && (lower === 'is' || lower.startsWith('is ') || lower.includes('income') || lower.includes('p&l') || lower.includes('profit'))) {
            isSheet = workbook.Sheets[name];
        }
        if (!bsSheet && (lower === 'bs' || lower.startsWith('bs ') || lower.includes('balance'))) {
            bsSheet = workbook.Sheets[name];
        }
    }

    // If only one sheet, try to use it
    if (sheetNames.length === 1 && !isSheet && !bsSheet) {
        isSheet = workbook.Sheets[sheetNames[0]];
    }

    const result = { is: null, bs: null };

    if (isSheet) {
        result.is = parseSheet(isSheet, 'IS');
    }

    if (bsSheet) {
        const parsed = parseSheet(bsSheet, 'BS');
        // Detect prior December for BS
        if (parsed.months.length > 0) {
            const firstMonth = parsed.months[0];
            const [fy, fm] = firstMonth.split('-').map(Number);
            // If first month is January, check for prior December column
            if (fm === 1) {
                const priorDec = `${fy - 1}-12`;
                // Already included if parser found it
                if (!parsed.months.includes(priorDec)) {
                    // Check columns before first January for a Dec value
                    // The parser may have included it already; flag if not found
                    parsed.flags.push({ type: 'info', severity: 'info', row: 0, label: `Prior December (${priorDec}) not found in BS — CFS opening balance may need manual entry` });
                }
            }
        }
        result.bs = parsed;
    }

    return result;
}

// Parse a single sheet from a buffer (for separate file uploads)
export function parseSingleSheet(buffer, statementType) {
    const workbook = XLSX.read(buffer, { type: 'array', cellFormula: true, cellStyles: false });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return parseSheet(sheet, statementType);
}
